import React, { useEffect, useState } from "react";
import { serviceConsumer } from "../../../../network/ServiceConsumer";
import Loader from "../../../../Components/Loader/Loader";

// FLC-76: read-only Check-In pop-over for the Tabulate screen. Shows the
// selected competition's competitors and their digital check-in status
// (present / not checked in). The status comes from the same data the
// side-stage Check-In screen writes: registration.checked[] = [{competitionId,
// check}], surfaced by GET /feis/competition/dancers/:compId. Read-only — it
// does not change check-in status, only displays it while tabulating.

const overlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1050,
};

const cardStyle = {
    background: "#fff",
    borderRadius: "8px",
    padding: "20px 24px",
    width: "640px",
    maxWidth: "94vw",
    maxHeight: "86vh",
    overflowY: "auto",
    boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
};

const closeBtn = {
    minWidth: "44px",
    minHeight: "44px",
    border: "none",
    background: "transparent",
    fontSize: "22px",
    lineHeight: "1",
    cursor: "pointer",
    color: "#424242",
};

const footerBtn = {
    padding: "8px 18px",
    border: "1px solid #ccc",
    background: "#fff",
    borderRadius: "4px",
    cursor: "pointer",
    minHeight: "40px",
};

const th = { textAlign: "left", fontSize: "12px", fontWeight: 600, padding: "8px 6px", borderBottom: "1.5px solid #000" };
const td = { fontSize: "12px", padding: "8px 6px", borderBottom: "1px solid #DCDCDC" };

const CheckInPopover = ({ compId, compLabel, onClose }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        let active = true;
        const load = async () => {
            try {
                setLoading(true);
                const url = `${process.env.REACT_APP_BASE_URL}/feis/competition/dancers/${compId}`;
                const res = await serviceConsumer("GET", url);
                if (active) setRows(res?.result || []);
            } catch (e) {
                if (active) setErr("Could not load check-in data.");
            } finally {
                if (active) setLoading(false);
            }
        };
        if (compId) load();
        return () => { active = false; };
    }, [compId]);

    // Esc closes the pop-over.
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const isPresent = (reg) =>
        (reg?.checked || []).some(
            (i) => String(i.competitionId) === String(compId) && i.check
        );

    const sorted = [...rows].sort(
        (a, b) => Number(a?.card_number) - Number(b?.card_number)
    );
    const total = sorted.length;
    const presentCount = sorted.filter(isPresent).length;

    return (
        <div style={overlayStyle} onClick={onClose}>
            <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <div style={{ fontSize: "16px", fontWeight: 600, color: "#323232" }}>
                            Check-In — {compLabel}
                        </div>
                        {!loading && total > 0 && (
                            <div style={{ fontSize: "13px", color: "#424242", marginTop: "2px" }}>
                                {presentCount} of {total} checked in
                            </div>
                        )}
                    </div>
                    <button style={closeBtn} onClick={onClose} aria-label="Close check-in">×</button>
                </div>

                <hr style={{ margin: "12px 0" }} />

                {loading && <Loader />}

                {!loading && err && (
                    <div style={{ color: "#b00020", padding: "12px 0" }}>{err}</div>
                )}

                {!loading && !err && total === 0 && (
                    <div className="d-flex justify-content-center my-4" style={{ color: "#424242" }}>
                        No competitors registered for this competition.
                    </div>
                )}

                {!loading && !err && total > 0 && presentCount === 0 && (
                    <div style={{ background: "#FFF8E1", border: "1px solid #FFE082", borderRadius: "4px", padding: "8px 12px", fontSize: "12px", color: "#6d4c00", marginBottom: "10px" }}>
                        No digital check-ins recorded yet. If this event checks in on paper,
                        status here will stay "Not checked in".
                    </div>
                )}

                {!loading && !err && total > 0 && (
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr>
                                <th style={{ ...th, width: "12%" }}>#</th>
                                <th style={{ ...th, width: "30%" }}>Competitor</th>
                                <th style={{ ...th, width: "28%" }}>School</th>
                                <th style={{ ...th, width: "12%" }}>DOB</th>
                                <th style={{ ...th, width: "18%" }}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((reg) => {
                                const present = isPresent(reg);
                                return (
                                    <tr key={reg?._id}>
                                        <td style={td}>{reg?.card_number}</td>
                                        <td style={td}>{reg?.dancerData?.fname} {reg?.dancerData?.lname}</td>
                                        <td style={td}>{reg?.dancerData?.school}</td>
                                        <td style={td}>{reg?.dancerData?.birthday}</td>
                                        <td style={{ ...td, color: present ? "#2e7d32" : "#9e9e9e", fontWeight: present ? 600 : 400 }}>
                                            {present ? "✓ Present" : "Not checked in"}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}

                <div className="d-flex justify-content-end" style={{ marginTop: "16px" }}>
                    <button style={footerBtn} onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
};

export default CheckInPopover;
